// IE Hub - Help Dialog Component
// Responsive help dialog: mobile (bottom slide, full page) vs desktop (right slide, partial)

import React, { useRef, useState } from "react";
import {
  Box,
  CloseIcon,
  Dialog,
  DialogContent,
  Drawer,
  IconButton,
  Slide,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "../ui";
import { TransitionProps } from "@mui/material/transitions";

interface HelpDialogProps {
  open: boolean;
  onClose: () => void;
  zIndex?: number;
}

// Slide transition for mobile (from bottom)
const SlideUpTransition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return (
    <Slide direction="up" ref={ref} {...props} appear={props.appear ?? true} />
  );
});

export default function HelpDialog({
  open,
  onClose,
  zIndex,
}: HelpDialogProps): JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Touch gesture handling for mobile swipe-to-dismiss
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragDistance, setDragDistance] = useState(0);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Minimum swipe distance (in px) to trigger close
  const minSwipeDistance = 50;

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    const touch = e.targetTouches[0];
    if (touch) {
      setTouchStart(touch.clientY);
      setIsDragging(true);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;

    const touch = e.targetTouches[0];
    if (!touch) return;

    const currentTouch = touch.clientY;
    const distance = currentTouch - touchStart;

    // Only allow downward swipes (positive distance)
    if (distance > 0) {
      setDragDistance(distance);
      setTouchEnd(currentTouch);
    }
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      setIsDragging(false);
      setDragDistance(0);
      return;
    }

    const distance = touchEnd - touchStart;

    // Close dialog if swiped down enough
    if (distance > minSwipeDistance) {
      onClose();
    }

    // Reset states
    setTouchStart(null);
    setTouchEnd(null);
    setIsDragging(false);
    setDragDistance(0);
  };

  const helpContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* Header with Close Button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: { xs: 2, md: 3 },
          borderBottom: "1px solid",
          borderColor: "divider",
          minHeight: "64px",
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: "text.primary" }}
        >
          Help & support
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            p: 1,
            backgroundColor: "action.hover",
            "&:hover": {
              backgroundColor: "action.selected",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          p: { xs: 2, md: 3 },
          overflow: "auto",
        }}
      >
        <Stack spacing={3}>
          {/* Getting Started Section */}
          <Box>
            <Typography
              variant="h6"
              sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
            >
              Getting started
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
              Welcome to IE Hub! This onboarding process will help you set up
              your account and get started with our platform.
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", lineHeight: 1.5 }}
            >
              Fill in your personal information to continue with the
              registration process.
            </Typography>
          </Box>

          {/* Common Questions */}
          <Box>
            <Typography
              variant="h6"
              sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
            >
              Common questions
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  Why do you need my personal information?
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", lineHeight: 1.5 }}
                >
                  We collect this information to verify your identity and ensure
                  the security of your account.
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  Is my data secure?
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", lineHeight: 1.5 }}
                >
                  Yes, we use industry-standard encryption and security measures
                  to protect your personal information.
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  What happens next?
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", lineHeight: 1.5 }}
                >
                  After completing this form, you&apos;ll proceed through
                  additional verification steps to activate your account.
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Contact Information */}
          <Box>
            <Typography
              variant="h6"
              sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
            >
              Need more help?
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", lineHeight: 1.5, mb: 2 }}
            >
              If you have additional questions or need assistance, you can reach
              out to our support team:
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2" sx={{ color: "text.primary" }}>
                📧 Email: partnerhpa@uw.co.uk
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Box>
  );

  if (isMobile) {
    // Mobile: Full-screen dialog sliding from bottom with swipe-to-dismiss
    return (
      <Dialog
        open={open}
        onClose={onClose}
        TransitionComponent={SlideUpTransition}
        fullScreen
        keepMounted
        sx={{
          ...(zIndex !== undefined && { zIndex }),
          "& .MuiDialog-paper": {
            backgroundColor: "background.default",
            backgroundImage: "none",
            margin: 0,
            borderRadius: "16px 16px 0 0", // Top corners rounded for mobile
            transform: isDragging
              ? `translateY(${Math.min(dragDistance, 100)}px)`
              : "none",
            transition: isDragging ? "none" : "transform 0.3s ease-out",
            opacity: isDragging ? Math.max(1 - dragDistance / 200, 0.7) : 1,
          },
        }}
      >
        <DialogContent
          ref={dialogRef}
          sx={{ p: 0, height: "100%" }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Swipe Indicator */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              pt: 1,
              pb: 0.5,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 4,
                backgroundColor: "action.disabled",
                borderRadius: 2,
                opacity: 0.6,
              }}
            />
          </Box>
          {helpContent}
        </DialogContent>
      </Dialog>
    );
  }

  // Desktop: Drawer sliding from right
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      keepMounted
      sx={{
        ...(zIndex !== undefined && { zIndex }),
        "& .MuiDrawer-paper": {
          width: { md: "400px", lg: "480px" },
          maxWidth: "40vw",
          backgroundColor: "background.paper",
          border: "none",
          boxShadow: theme.shadows[8],
          borderRadius: "16px 0 0 16px", // Left side rounded for desktop
        },
      }}
    >
      {helpContent}
    </Drawer>
  );
}
